import { createUseStyles } from 'react-jss';

import { ITheme } from 'types';
import {
  StackingContext,
  zLoaderContainer,
  zEraserPage,
  zVisibilitySidebarIcon,
} from 'utils/ui-helpers/stackingContexts';

import {
  ERASER_SIDEBAR_WIDTH,
  ERASER_SIDEBAR_WIDTH_DARK,
  ERASER_HEADER_HEIGHT,
  ERASER_HEADER_HEIGHT_DARK,
  ERASER_SIDEBAR_DIFF,
  ERASER_PAGE_PADDING,
  ERASER_GAP,
  DISTORTION_SKELETON_SIZE, ERASER_FOOTER_HEIGHT,
} from './config';

const useStyles = createUseStyles<ITheme>(theme => ({
  container: {
    display: 'flex',
  },
  loaderContainer: ({ isDarkMode }) => {
    const styleGL = {
      position: 'absolute',
      zIndex: zLoaderContainer,
      top: '50%',
    };

    if (!isDarkMode) {
      return {
        ...styleGL,
        transform: 'translate(0, -50%)',
      };
    }

    return {
      ...styleGL,
      left: '50%',
      transform: 'translate(50%, -50%)',
    };
  },
  loaderIcon: {
    '& img': {
      width: '41px!important',
      height: '41px!important',
      position: 'inherit',
      top: 'auto',
      left: 'auto',
      transform: 'none',
    },
  },
  skeletonContainer: ({ width, height }) => ({
    width: `${width + DISTORTION_SKELETON_SIZE}px`,
    height: `${height + DISTORTION_SKELETON_SIZE}px`,
    position: 'absolute',
    zIndex: StackingContext.Base,
    top: '50%',
    transform: `translate(-50%, -${(height + DISTORTION_SKELETON_SIZE + ERASER_FOOTER_HEIGHT) / 2}px)`,
    left: '50%',
  }),
  EraserPage: ({ isDarkMode }) => {
    const styleGl = {
      position: 'absolute',
      zIndex: zEraserPage,
      width: '100%',
      height: '100%',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        backgroundColor: '#ffffff',
      };
    }

    return {
      ...styleGl,
      padding: ERASER_PAGE_PADDING,
      backgroundColor: theme.colors.backgrounds.tint2,
    };
  },
  eraserStageMain: ({ isDarkMode }) => {
    const styleGl = {
      width: '100%',
      display: 'flex',
      position: 'relative',
      flexDirection: 'column',
      flexShrink: 1,
      overflow: 'hidden',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        alignItems: 'center',
        padding: `20px ${theme.spacings.s0}`,
        height: `calc(100vh - ${ERASER_HEADER_HEIGHT}px)`,
        backgroundColor: '#f1f1f6',
      };
    }

    return {
      ...styleGl,
      height: `calc(100vh - ${ERASER_HEADER_HEIGHT_DARK}px - ${ERASER_PAGE_PADDING * 2 + ERASER_GAP}px)`,
    };
  },
  eraserStageMainZoomed: {
    padding: theme.spacings.s0,
  },
  eraserStageWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eraserStageContainer: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
  },
  eraserStageContainerNotZoom: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  eraserStageContainerVertical: {
    margin: 'auto',
  },
  eraserStageContainerCenter: {
    justifyContent: 'center',
  },
  canvasFooterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginInlineStart: theme.spacings.s8,
  },
  canvasFooterWrapper: {
    display: 'grid',
    gridAutoFlow: 'column',
    gap: theme.spacings.s4,
    justifyContent: 'center',
    pointerEvents: 'all',
    flexWrap: 'wrap',
    '& [class*="pw-dropdown_label"]': {
      background: '#ffffff',
      color: '#626777',
      fontWeight: 600,
      '& path': {
        fill: '#626777',
      },
      '&::before': {
        border: 'none',
      },
    },
  },
  canvasFooter: ({ isDarkMode, isZoomed }) => {
    const styleGl = {
      position: 'absolute',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        bottom: 20,
      };
    }

    return {
      ...styleGl,
      bottom: theme.spacings.s0,
      marginLeft: theme.spacings.s0,
      ...(isZoomed && { pointerEvents: 'none' }),
      left: theme.spacings.s8,
      right: theme.spacings.s0,
    };
  },
}));

const useHeaderStyles = createUseStyles<ITheme>(theme => ({
  eraserHeader: ({ isDarkMode }) => {
    const styleGl = {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        height: ERASER_HEADER_HEIGHT,
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #f1f1f6',
      };
    }

    return {
      ...styleGl,
      alignItems: 'center',
      height: ERASER_HEADER_HEIGHT_DARK,
      backgroundColor: theme.colors.backgrounds.tint1,
      borderRadius: theme.radiuses.r12,
      marginBottom: theme.spacings.s4,
      padding: `${theme.spacings.s0} ${theme.spacings.s16}`,
    };
  },
  eraser: {
    height: 20,
    float: 'left',
    fontSize: 16,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#282b36',
    margin: '21px 25px',
    fontWeight: 600,
  },
  cancelApplyContainer: {
    display: 'flex',
    verticalAlign: 'baseline',
  },
  cancel: {
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#282b36',
    fontSize: 14,
    lineHeight: '17px',
    cursor: 'pointer',
  },
  cancelNS: {
    marginRight: theme.spacings.s16,
  },
  mainSettings: {
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
  },
  apply: {
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `${theme.spacings.s8} 10px ${theme.spacings.s8} ${theme.spacings.s24}`,
    padding: `${theme.spacings.s0} 28px`,
    minWidth: 99,
    height: 46,
    color: '#ffffff',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '17px',
    backgroundColor: '#c209c1',
    borderRadius: theme.variables.mainBorderRadius,
    cursor: 'pointer',
  },
  hoverApply: {
    '&:hover': {
      backgroundColor: '#ac00a6',
    },
  },
  disabledButton: {
    ...theme.variables.effectItemDisabledState,
  },
  disabledApply: {
    backgroundColor: '#dcdde9',
  },
}));

const useSidebarStyles = createUseStyles((theme: ITheme) => ({
  eraserDrawnToolsContainer: ({ isDarkMode }) => {
    const styleGl = {
      position: 'relative',
      height: '100%',
      textAlign: 'center',
      zIndex: StackingContext.Base,
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        width: ERASER_SIDEBAR_WIDTH,
        backgroundColor: '#ffffff',
      };
    }

    return {
      ...styleGl,
      width: ERASER_SIDEBAR_WIDTH_DARK,
      margin: `${theme.spacings.s0} ${theme.spacings.s4} ${theme.spacings.s0} ${theme.spacings.s0}`,
    };
  },
  eraserDrawnSettingsContainer: ({ isDarkMode }) => {
    if (!isDarkMode) {
      return {
        borderLeft: '2px solid #f1f1f6',
      };
    }

    return {
      backgroundColor: theme.colors.backgrounds.tint1,
      borderRadius: theme.radiuses.r12,
      padding: `${theme.spacings.s16} ${theme.spacings.s16}`,
    };
  },
  eraserDrawnToolsContainerScroller: ({ isDarkMode }) => {
    const styleGl = {
      top: theme.spacings.s0,
      right: theme.spacings.s0,
      bottom: theme.spacings.s0,
      left: theme.spacings.s0,
      width: '100%',
      height: '100%',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
      overflowY: 'auto',
      '-weibkit-overflow-scrolling': 'touch',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        position: 'absolute',
        backgroundColor: '#ffffff',
      };
    }

    return {
      ...styleGl,
      backgroundColor: theme.colors.backgrounds.tint1,
      borderRadius: theme.radiuses.r12,
      '--list-item-gap': theme.spacings.s4,
      position: 'relative',
      padding: theme.spacings.s8,
    };
  },
  sliderBarToolsContainer: {
    marginTop: ({ isDarkMode }) => !isDarkMode ? 5 : 0,
  },
  deselectedTool: {
    color: '#16191d',
  },
  toolIconName: {
    marginTop: 4,
    fontSize: 12,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    fontWeight: 500,
    lineHeight: '16px',
    color: '#545869',
  },
  hoverIconName: {
    color: '#282b36',
  },
  mainSettings: {
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sidebarTools: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 17,
    marginTop: 17,
    width: 80,
    marginLeft: 3,
  },
  toolContainer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 70,
    height: 64,
    cursor: 'pointer',
    fontSize: 12,
    color: '#545869',
  },
  sidebarCategoryIndicator: {
    ...theme.variables.sidebarCategoryIndicator,
    left: theme.spacings.s0,
  },
  hoverToolContainer: {
    '&:hover': {
      '& path': {
        fill: '#282b36',
      },
      '& p': {
        color: '#282b36',
      },
    },
  },
  hoverCutoutContainer: {
    '&:hover': {
      '& path': {
        stroke: theme.colors.texts.base,
      },
    },
  },
  disabledBrush: {
    opacity: 0.5,
    cursor: 'default',
  },
  eraserSidebarContainer: {
    display: 'flex',
    flexShrink: 0,
  },
  visibilitySidebarButton: {
    ...theme.variables.visibilitySidebarLayerIconNs,
    transform: `translate(0, -${ERASER_HEADER_HEIGHT_DARK + 2 * ERASER_PAGE_PADDING + ERASER_GAP - theme.variables.visibilitySidebarLayerIconNs.height}px)`,
    '@media(hover: hover)': {
      '&:hover': {
        '& $arrowIconNS path': {
          fill: theme.colors.texts.base,
        },
      },
    },
  },
  arrowIconNS: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: ({ open }) => open ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) rotate(180deg)',
    width: 16,
    height: 16,
    transition: 'transform .6s ease',
    '& path': {
      fill: theme.colors.texts.tint1,
      transition: 'fill 1s ease',
    },
  },
  visibilitySidebarIcon: ({ isDarkMode }) => {
    const styleGl = {
      ...theme.variables.visibilitySidebarLayerIcon,
      position: 'fixed',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      zIndex: zVisibilitySidebarIcon,
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        width: 26,
        height: 56,
        left: `${theme.variables.sidebarCategoryWidth + theme.variables.sidebarItemsWidth}px`,
      };
    }

    return {
      ...styleGl,
      width: 24,
      height: 56,
      left: `${theme.variables.sidebarCategoryWidth + theme.variables.sidebarItemsWidth + ERASER_PAGE_PADDING + ERASER_GAP}px`,
      ...{ backgroundColor: theme.colors.backgrounds.tint1 },
    };
  },
  arrowIcon: {
    ...theme.variables.arrowIcon,
  },
  selectedToolLeftIcon: {
    height: 46,
    width: 3,
    position: 'absolute',
    left: theme.spacings.s0,
    marginTop: -1,
  },
  toolIcons: {
    verticalAlign: 'top',
    display: 'inline-block',
    width: theme.variables.iconSize,
    height: theme.variables.iconSize,
    '& path': {
      fill: theme.colors.texts.tint1,
    },
  },
  activeToolIcon: {
    '& path': {
      fill: theme.colors.accents.primary.base.default,
    },
  },
  sidebarTooltipItem: {
    '& + &': {
      marginTop: `${theme.spacings.s4}`,
    },
  },
}));

const useStageStyles = createUseStyles({
  stage: {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'none',
  },
});

const useHistoryStyles = createUseStyles({
  historyTools: {
    marginBottom: ({ isDarkMode }) => !isDarkMode ? 22 : 0,
    left: ({ isDarkMode }) => !isDarkMode ? 16 : 0,
  },
});

const useSettingsStyle = createUseStyles((theme: ITheme) => ({
  settingsContainer: {
    margin: ({ isDarkMode }) => !isDarkMode
      ? `${theme.spacings.s24} ${theme.spacings.s24} ${theme.spacings.s0} 26px`
      : `${theme.spacings.s8} ${theme.spacings.s0} ${theme.spacings.s0} ${theme.spacings.s0}`,
    background: ({ isDarkMode }) => !isDarkMode ? '#ffffff' : theme.colors.backgrounds.tint1,
  },
  settingIcon: {
    fontSize: 20,
  },
  rangeWrapper: ({ isDarkMode }) => {
    const styleGl = {
      alignItems: 'center',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        display: 'flex',
      };
    }

    return {
      ...styleGl,
      display: 'grid',
      width: 208,
    };
  },
  inputRange: {
    position: 'relative',
    borderRadius: theme.variables.mainBorderRadius,
    cursor: 'pointer',
    '-webkit-appearance': 'none',
    height: 1,
    outline: 'none',
    width: 244,
    zIndex: StackingContext.Above,
    '&::-webkit-slider-thumb': {
      '-webkit-appearance': 'none',
      appearance: 'none',
      width: 14,
      height: 14,
      cursor: 'pointer',
      borderRadius: 2,
      boxSizing: 'border-box',
      background: '#ffffff',
      border: '1px solid #535771',
    },
    '&::-moz-range-thumb': {
      '-webkit-appearance': 'none',
      appearance: 'none',
      width: 14,
      height: 14,
      cursor: 'pointer',
      borderRadius: 2,
      boxSizing: 'border-box',
      background: '#ffffff',
      border: '1px solid #535771',
    },
    '&::-moz-focus-outer': {
      border: 'none',
    },
  },
  inputRangContainer: ({ isDarkMode }) => {
    const styleGl = {
      display: 'flex',
      justifyContent: 'space-around',
    };

    if (!isDarkMode) {
      return {
        ...styleGl,
        flexDirection: 'column',
        marginBottom: 12,
      };
    }

    return {
      ...styleGl,
      flexDirection: 'row',
      marginBottom: theme.spacings.s12,
      alignItems: 'center',
    };
  },
  rangeWrap: {
    position: 'relative',
    '&::before, &::after': {
      content: "''",
      display: 'block',
      height: '1px',
      position: 'absolute',
      top: '50%',
      left: '0',
      right: '0',
      opacity: '0.7',
      transform: 'translateY(-50%)',
    },
    '&::before': {
      background: '#000000',
      width: 'var(--progressValue)',
      zIndex: StackingContext.Above,
    },
    '&::after': {
      background: '#babcc5',
    },
  },
  settingName: ({ isDarkMode }) => {
    if (!isDarkMode) {
      return {
        fontSize: 12,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '16px',
        userSelect: 'none',
        marginBottom: -6,
        float: 'left',
        color: '#545869',
      };
    }

    return {
      width: 88,
      marginRight: theme.spacings.s12,
    };
  },
  settingValue: {
    float: 'right',
    width: 58,
    height: 40,
    marginLeft: 8,
    display: 'flex',
    textAlign: 'center',
    border: '1px solid #dcdde9',
    borderRadius: theme.radiuses.r4,
    fontSize: 14,
    color: '#16191d',
    fontStyle: 'normal',
    lineHeight: '17px',
    '-moz-appearance': 'textfield',
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: theme.spacings.s0,
      '-moz-appearance': 'textfield',
    },
    '&:hover': {
      border: '1px solid #bcc0d1',
    },
    '&:focus': {
      border: '1px solid #282b36',
    },
  },
  cutoutSettingsContainer: {
    display: 'flex',
    margin: ({ isDarkMode }) => !isDarkMode ? `22px ${theme.spacings.s8} ${theme.spacings.s0} 20px` : theme.spacings.s0,
  },
  currentImgContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 154,
    height: 154,
    marginRight: 12,
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    border: '2px solid transparent',

    '& object': {
      width: '100%',
      height: '100%',
    },
  },
  cutoutImageContainer: {
    '&:first-of-type': {
      marginRight: 8,
    },
  },
  cutoutPaperTitleContainer: {
    padding: `${theme.spacings.s0} ${theme.spacings.s4} 6px ${theme.spacings.s4}`,
    position: 'absolute',
    bottom: theme.spacings.s0,
    width: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.variables.mainBorderRadius,
    position: 'absolute',
  },
  applyHover: {
    '&:hover': {
      border: '2px solid #dcdde9',
      borderRadius: theme.variables.mainBorderRadius,
      cursor: 'pointer',

      '&:before': {
        content: '""',
        width: '100%',
        height: '100%',
        border: '4px solid #ffffff',
        zIndex: StackingContext.Above,
        borderRadius: theme.variables.mainBorderRadius,
      },
      '& object': {
        width: '100%',
        height: '100%',
      },
    },
  },
  disabledState: {
    ...theme.variables.effectItemDisabledState,
  },
  appliedRemoverTool: {
    cursor: 'default',
    width: 154,
    height: 154,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.variables.mainBorderRadius,
    position: 'relative',
    border: '2px solid #282b36',
    backgroundColor: '#ffffff',
    '&:before': {
      content: '""',
      width: '100%',
      height: '100%',
      border: '4px solid #ffffff',
      zIndex: StackingContext.Above,
      borderRadius: theme.variables.mainBorderRadius,
    },

    '& object': {
      width: '100%',
      height: '100%',
    },
  },
  cutoutTitle: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    left: 10,
    bottom: 10,
    color: '#ffffff',
    width: 66,
    height: 20,
    background: '#282b36',
    borderRadius: 3,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 9,
    lineHeight: '11px',
  },
  crown: {
    zIndex: StackingContext.Above,
  },
  blank: {
    width: `calc(var(--sidebar-width, 444px) - ${ERASER_SIDEBAR_DIFF}px)`,
  },
}));

export {
  useStageStyles,
  useHistoryStyles,
  useHeaderStyles,
  useSidebarStyles,
  useSettingsStyle,
};

export default useStyles;
